import { useEffect, useMemo, useState } from 'react'

import Button from 'common/components/button'
import Checkbox from 'common/components/checkbox'
import Loader from 'common/components/loaders/Loader'
import { useAppSelector } from 'common/hooks/redux'
import useProjectsQuery from 'features/projects/queries/useProjectsQuery'

import { ScoreExperiment } from '../api/api'
import useGetProjectExperimentsQuery from '../api/queries/useGetProjectExperimentsQuery'

type Props = {
  onScore: (experiments: ScoreExperiment[], projectId: string) => void
  onClear: () => void
  isScoring: boolean
}

const PreGeneratedSetup = ({ onScore, onClear, isScoring }: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [selectedExperimentIds, setSelectedExperimentIds] = useState<string[]>(
    []
  )

  const { data: projects, isLoading: isLoadingProjects } =
    useProjectsQuery(accountId)

  const {
    data: experiments,
    isLoading: isLoadingExperiments,
    isError: isExperimentsError,
  } = useGetProjectExperimentsQuery(selectedProjectId)

  // Reset experiment selection when the project changes
  useEffect(() => {
    setSelectedExperimentIds([])
  }, [selectedProjectId])

  const activeProjects = useMemo(
    () => projects?.filter((p) => !p.isDeleted && !p.isArchived) ?? [],
    [projects]
  )

  const selectedExperiments = useMemo(
    () =>
      experiments?.filter((e) => selectedExperimentIds.includes(e.id)) ?? [],
    [experiments, selectedExperimentIds]
  )

  const allSelected =
    !!experiments?.length &&
    experiments.every((e) => selectedExperimentIds.includes(e.id))

  const toggleExperiment = (id: string) => {
    setSelectedExperimentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedExperimentIds([])
    } else {
      setSelectedExperimentIds(experiments?.map((e) => e.id) ?? [])
    }
  }

  const handleClear = () => {
    setSelectedProjectId('')
    setSelectedExperimentIds([])
    onClear()
  }

  return (
    <>
      <div className="mb-4">
        <label className="text-xs font-semibold text-coolGray-500 mb-1 block">
          Project
        </label>
        <select
          className="w-full text-sm border border-gold-200 rounded-md px-3 py-2 bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-200 outline-none"
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          disabled={isLoadingProjects}
        >
          <option value="">
            {isLoadingProjects ? 'Loading projects…' : '— Select a project —'}
          </option>
          {activeProjects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {selectedProjectId && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-coolGray-500">
              Experiments
            </label>
            {!!experiments?.length && (
              <Button
                variant="link"
                size="small"
                onClick={toggleSelectAll}
                disabled={isScoring}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </Button>
            )}
          </div>

          <Loader isLoading={isLoadingExperiments}>
            {isExperimentsError ? (
              <p className="text-xs text-red-500 py-2">
                Failed to load experiments for this project.
              </p>
            ) : experiments?.length ? (
              <div className="flex flex-col gap-1">
                {experiments.map((experiment) => (
                  <div
                    key={experiment.id}
                    className="flex items-center gap-3 px-3 py-2 bg-gold-50 border border-gold-200 rounded-md"
                  >
                    <Checkbox
                      isChecked={selectedExperimentIds.includes(experiment.id)}
                      onChange={() => toggleExperiment(experiment.id)}
                      isDisabled={isScoring}
                      label={
                        <span className="flex items-center gap-2 flex-1">
                          <span className="text-sm font-medium text-coolGray-800">
                            {experiment.name}
                          </span>
                          {experiment.distributionChannel && (
                            <span className="text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              {experiment.distributionChannel}
                            </span>
                          )}
                        </span>
                      }
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-coolGray-400 py-2">
                No experiments with generated language found for this project.
              </p>
            )}
          </Loader>
        </div>
      )}

      {selectedExperiments.length > 0 && (
        <div className="mb-4">
          <label className="text-xs font-semibold text-coolGray-500 mb-2 block">
            Selected for scoring
          </label>
          <div className="flex flex-col gap-1">
            {selectedExperiments.map((experiment) => (
              <div
                key={experiment.id}
                className="flex items-center gap-2 px-3 py-2 bg-gold-50 border border-gold-200 rounded-md text-sm"
              >
                <span className="flex-1 font-medium">{experiment.name}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-coolGray-400 mt-2">
            Existing language from the selected experiments will be loaded and
            scored against the brand voice configuration.
          </p>
        </div>
      )}

      <div className="flex gap-2 items-center">
        <Button
          variant="primary"
          onClick={() => onScore(selectedExperiments, selectedProjectId)}
          loading={isScoring}
          disabled={selectedExperiments.length === 0 || isScoring}
        >
          Score language
        </Button>
        <Button variant="secondary" onClick={handleClear} disabled={isScoring}>
          Clear
        </Button>
      </div>
    </>
  )
}

export default PreGeneratedSetup
